import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import React, { useMemo } from 'react';
import { useInsurancePolicySummaryQuery } from '../../../../queries/InsurancePolicy/useInsurancePolicySummary';
import { usePoliciesReportingChartsQuery } from '../../../../queries/Policies/usePoliciesReportingCharts';
import { Alert, AlertActions, AlertDescription, AlertTitle } from '../../../components-v2/Alert';
import { Button } from '../../../components-v2/Button';
import { DataDisplay } from '../components/DataDisplay';
import { formatCurrency } from '../helpers/formatCurrency';
import { styleSheet as advancedReportingStyleSheet } from '../styles';
import { CommissionsByOwnerTreemapChart } from './components/CommissionsByOwnerChart';
import { PoliciesByLineOfBusinessChart } from './components/PoliciesByLineOfBusinessChart';
import { PoliciesFilters } from './components/PoliciesFilters';
import { PremiumAndAverageByCarrierChart } from './components/PremiumAndAverageByCarrierChart';
import { PremiumDistributionChart } from './components/PremiumDistributionChart';
import { PremiumOverTimeChart } from './components/PremiumOverTimeChart';
import { getRenewalChartLabels } from './renewalChartLabels';
import { styleSheet } from './styles';
import { usePolicyFilterCriteria } from './usePolicyFilterCriteria';

const charts: Api.IInsurancePolicyChartConfiguration[] = [
	{
		key: 'premiumOverTime',
		title: 'Upcoming Renewals by Month',
		groupBy: 'Date',
		aggregation: 'Sum',
		aggregateField: 'premium',
		dateGranularity: 'Month',
		sortField: 'Label',
		sortAscending: true,
		visualizationType: 'Line',
	},
	{
		key: 'premiumByCarrier',
		title: 'Upcoming Renewal Premium by Carrier',
		groupBy: 'Carrier',
		aggregation: 'Sum',
		aggregateField: 'premium',
		limit: 10,
		sortField: 'Value',
		sortAscending: false,
		visualizationType: 'Pie',
	},
	{
		key: 'policiesByLineOfBusiness',
		title: 'Policies by Line of Business',
		groupBy: 'LineOfBusiness',
		aggregation: 'Count',
		sortField: 'Value',
		sortAscending: false,
		visualizationType: 'Bar',
	},
	{
		key: 'premiumDistribution',
		title: 'Upcoming Renewals by Premium Range',
		groupBy: 'PremiumRange',
		aggregation: 'Count',
		visualizationType: 'Bar',
	},
	{
		key: 'commissionsByOwner',
		title: 'Commissions by Policy Owner',
		groupBy: 'Owner',
		aggregation: 'Sum',
		aggregateField: 'commissions',
		limit: 5,
		sortField: 'Value',
		sortAscending: false,
		visualizationType: 'Bar',
	},
	{
		key: 'averagePremiumByCarrier',
		title: 'Average Premium by Carrier',
		groupBy: 'Carrier',
		aggregation: 'Average',
		aggregateField: 'premium',
		limit: 10,
		sortField: 'Value',
		sortAscending: false,
		visualizationType: 'Bar',
	},
];

const defaultFilterCriteria: Api.IInsurancePolicyFilterCriteria[] = [
	{
		property: Api.InsurancePolicyFilterProperty.RenewalDate,
		op: Api.FilterOperator.Gt,
		value: moment().format('YYYY-MM-DD'),
	},
];

interface ICurrentBookTabProps {
	onViewHistory?: () => void;
}

export const CurrentBookTab = ({ onViewHistory }: ICurrentBookTabProps) => {
	const { normalizedCriteria, handleFilterChange } = usePolicyFilterCriteria('filterCriteria', defaultFilterCriteria);

	const renewalLabels = useMemo(() => getRenewalChartLabels(normalizedCriteria), [normalizedCriteria]);
	const titledCharts = useMemo(() => {
		const renewalTitles: Record<string, string> = {
			premiumOverTime: renewalLabels.overTimeTitle,
			premiumDistribution: renewalLabels.distributionTitle,
			premiumByCarrier: renewalLabels.byCarrierTitle,
		};
		return charts.map(chart => ({ ...chart, title: renewalTitles[chart.key] ?? chart.title }));
	}, [renewalLabels]);

	const policiesReportingChartsQuery = usePoliciesReportingChartsQuery({
		enabled: true,
		filterRequest: {
			criteria: normalizedCriteria,
		} as Api.IInsurancePolicyFilterRequest,
		charts: titledCharts,
	});

	const chartsData = policiesReportingChartsQuery.data;

	const policySummaryQuery = useInsurancePolicySummaryQuery({
		enabled: true,
		filterRequest: {
			criteria: normalizedCriteria,
		} as Api.IInsurancePolicyFilterRequest,
	});

	const averagePremiumPerPolicy = useMemo(() => {
		if (!policySummaryQuery?.data || policySummaryQuery?.data?.totalPolicies === 0) {
			return 0;
		}

		return formatCurrency(policySummaryQuery?.data?.totalAmount / policySummaryQuery?.data?.totalPolicies);
	}, [policySummaryQuery?.data]);

	return (
		<>
			<div className={css(advancedReportingStyleSheet.filterSection)}>
				<div className={css(advancedReportingStyleSheet.filterHeader)}>
					<PoliciesFilters filterCriteria={normalizedCriteria} onFilterChange={handleFilterChange} />
				</div>
				{renewalLabels.includesPastRenewals ? (
					<Alert styles={[styleSheet.pastRenewalsAlert]}>
						<AlertTitle>These charts show your book as it is today</AlertTitle>
						<AlertDescription>
							Past renewal dates show today’s policies, not your book as it was then. Policies that have since renewed,
							lapsed or been cancelled won’t appear.
						</AlertDescription>
						{onViewHistory ? (
							<AlertActions>
								<Button type='button' variant='outline' size='sm' onClick={onViewHistory}>
									View book history
								</Button>
							</AlertActions>
						) : null}
					</Alert>
				) : null}
			</div>

			<div id='exportable-content'>
				<div className={css(advancedReportingStyleSheet.container)}>
					<DataDisplay
						title='Total Policies'
						data={policySummaryQuery?.data?.totalPolicies ?? 0}
						loading={policySummaryQuery?.isLoading}
					/>
					<DataDisplay
						title='Total Premium'
						data={formatCurrency(policySummaryQuery?.data?.totalAmount ?? 0)}
						loading={policySummaryQuery?.isLoading}
					/>
					<DataDisplay
						title='Average Premium Per Policy'
						data={averagePremiumPerPolicy}
						loading={policySummaryQuery?.isLoading}
						className={css(advancedReportingStyleSheet.bigDataDisplay)}
					/>
				</div>

				<div className={css(advancedReportingStyleSheet.chartsSection)}>
					<PremiumOverTimeChart
						data={chartsData?.premiumOverTime}
						isLoading={policiesReportingChartsQuery.isLoading}
						subtitle={renewalLabels.subtitle}
					/>
					<CommissionsByOwnerTreemapChart
						data={chartsData?.commissionsByOwner}
						isLoading={policiesReportingChartsQuery.isLoading}
					/>
				</div>

				<div className={css(advancedReportingStyleSheet.chartsSection)}>
					<PoliciesByLineOfBusinessChart
						data={chartsData?.policiesByLineOfBusiness}
						isLoading={policiesReportingChartsQuery.isLoading}
					/>
					<PremiumDistributionChart
						data={chartsData?.premiumDistribution}
						isLoading={policiesReportingChartsQuery.isLoading}
						subtitle={renewalLabels.subtitle}
					/>
				</div>

				<div className={css(advancedReportingStyleSheet.chartsSection)}>
					<PremiumAndAverageByCarrierChart
						premiumByCarrier={chartsData?.premiumByCarrier}
						averagePremiumByCarrier={chartsData?.averagePremiumByCarrier}
						isLoading={policiesReportingChartsQuery.isLoading}
						subtitle={renewalLabels.subtitle}
					/>
				</div>
			</div>
		</>
	);
};
